import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { api } from '../context/AuthContext';

export default function ForgotPassword() {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/auth/password/forgot', { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: err?.response?.data?.detail || 'Try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
    // `api` is a module-level stable import — safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, toast]);

  return (
    <div className="bg-brand-cream min-h-screen">
      <section className="bg-brand-green text-brand-cream py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Account recovery</span>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">Forgot your password?</h1>
          <p className="mt-4 text-brand-cream/75">
            Enter the email on your account and we&rsquo;ll send you a link to reset your password.
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-md mx-auto px-5 sm:px-8">
          {sent ? (
            <div data-testid="forgot-sent" className="bg-white rounded-2xl border border-brand-cream-dark p-8 md:p-10 text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-brand-green flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-brand-cream" />
              </div>
              <h2 className="font-serif text-2xl text-brand-green">Check your inbox</h2>
              <p className="text-sm text-brand-green/75">
                If an account exists for <strong>{email}</strong>, a password-reset link is on its way.
                The link expires in 30 minutes.
              </p>
              <p className="text-xs text-brand-green/60">
                Didn&rsquo;t get it? Check your spam folder, then try again.
              </p>
              <Link to="/login" className="inline-block underline text-brand-green font-semibold text-sm">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-2xl border border-brand-cream-dark p-8 md:p-10 space-y-5">
              <div>
                <label htmlFor="forgot-email" className="block text-sm text-brand-green/70 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-green/50 pointer-events-none" />
                  <input
                    id="forgot-email"
                    data-testid="forgot-email-input"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@brokerage.com"
                    className="w-full pl-11 pr-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-brand-green text-brand-cream font-semibold hover:scale-[1.01] transition-transform disabled:opacity-70"
              >
                {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : (<>Send reset link <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <p className="text-center text-sm text-brand-green/70">
                Remembered it?{' '}
                <Link to="/login" className="underline text-brand-green font-semibold">Sign in</Link>
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
